import { useMemo, useState } from "react";
import type { MugsManualChange, MugsManualChangeDecision, MugsManualChangeKind, MugsManualChangeSummary } from "../../types";
import { Alert } from "../Alert";
import { Button } from "../Button";
import { DataGrid, type GridColumn } from "../DataGrid";
import { SelectInput } from "../fields";
import { EmptyState } from "../ScreenStates";

/** Labels the importer reads; the order is the order the groups appear in. */
const CHANGE_KIND_LABELS: Record<MugsManualChangeKind, string> = {
  NewCondition: "New conditions",
  NewRating: "New ratings and drugs",
  RemovedCondition: "Removed conditions",
  RemovedRating: "Removed ratings and drugs",
  DebitsChanged: "Updated debits",
  NotesChanged: "Updated notes",
  CodeChanged: "Updated codes",
  Renamed: "Renamed",
  TimeChanged: "Time changed",
  TimeBandsSplit: "Time bands split",
  DuplicateSourceRows: "Duplicate rows in the file",
};

const KIND_ORDER = Object.keys(CHANGE_KIND_LABELS) as MugsManualChangeKind[];

interface MugsManualChangePreviewProps {
  summary: MugsManualChangeSummary;
  changes: readonly MugsManualChange[];
  isLoading: boolean;
  /** Null while a decision is being saved for no row; otherwise the change id in flight. */
  decidingChangeId: string | null;
  onDecide: (change: MugsManualChange, decision: MugsManualChangeDecision) => void;
  children?: never;
}

function describeValues(change: MugsManualChange): string {
  if (change.changeKind === "NewCondition" || change.changeKind === "NewRating") return change.newValue ? `${change.newValue} debits` : "";
  if (change.changeKind === "RemovedCondition" || change.changeKind === "RemovedRating") return change.oldValue ? `${change.oldValue} debits` : "";
  if (change.oldValue === null && change.newValue === null) return "";
  return `${change.oldValue ?? "none"} → ${change.newValue ?? "none"}`;
}

/**
 * Every change starts approved; the importer reads the groups and flips a row to Ignore only when something looks wrong.
 * Debit changes on ratings with an active override say so, because the override stays whatever the release says.
 */
export function MugsManualChangePreview({ summary, changes, isLoading, decidingChangeId, onDecide }: MugsManualChangePreviewProps) {
  const [kindFilter, setKindFilter] = useState<MugsManualChangeKind | "all">("all");
  const presentKinds = useMemo(() => KIND_ORDER.filter((kind) => summary.counts.some((count) => count.kind === kind && count.total > 0)), [summary.counts]);
  const visible = useMemo(() => (kindFilter === "all" ? changes : changes.filter((change) => change.changeKind === kindFilter)), [changes, kindFilter]);
  const totalChanges = summary.counts.reduce((sum, count) => sum + count.total, 0);

  const columns = useMemo<GridColumn<MugsManualChange>[]>(
    () => [
      { key: "kind", header: "Change", sortValue: (row) => KIND_ORDER.indexOf(row.changeKind), render: (row) => CHANGE_KIND_LABELS[row.changeKind] },
      { key: "condition", header: "Condition", sortValue: (row) => row.conditionName, render: (row) => <span className="font-medium text-gray-800">{row.conditionName}</span> },
      { key: "rating", header: "Rating", sortValue: (row) => row.ratingText ?? "", render: (row) => row.ratingText ?? <span className="text-gray-400">–</span> },
      { key: "time", header: "Time", render: (row) => row.time ?? <span className="text-gray-400">–</span> },
      { key: "values", header: "Was → Now", render: (row) => <span className="tabular-nums">{describeValues(row)}</span> },
      {
        key: "override",
        header: "Override",
        render: (row) => (row.hasActiveModification ? <span className="rounded-full bg-amber-50 px-2 py-0.5 text-xs font-medium text-amber-800">Override stays in place</span> : <span className="text-gray-400">–</span>),
      },
      {
        key: "decision",
        header: "Decision",
        align: "right",
        render: (row) => (
          <Button
            variant={row.decision === "Ignore" ? "secondary" : "ghost"}
            size="sm"
            isLoading={decidingChangeId === row.id}
            onClick={() => onDecide(row, row.decision === "Ignore" ? "Approve" : "Ignore")}
            aria-label={`${row.decision === "Ignore" ? "Approve" : "Ignore"} ${row.conditionName}${row.ratingText ? `: ${row.ratingText}` : ""}`}
          >
            {row.decision === "Ignore" ? "Ignored" : "Approved"}
          </Button>
        ),
      },
    ],
    [decidingChangeId, onDecide],
  );

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap gap-2" aria-label="Change counts">
        {presentKinds.map((kind) => {
          const count = summary.counts.find((entry) => entry.kind === kind);
          return (
            <button key={kind} type="button" onClick={() => setKindFilter(kindFilter === kind ? "all" : kind)} aria-pressed={kindFilter === kind} className={`cursor-pointer rounded-full border px-3 py-1 text-xs font-medium focus:outline-none focus:ring-2 focus:ring-brand-green-50 ${kindFilter === kind ? "border-brand-green bg-brand-green-50 text-brand-green-dark" : "border-gray-200 bg-white text-gray-700 hover:bg-gray-50"}`}>
              {CHANGE_KIND_LABELS[kind]} <span className="ml-1 tabular-nums">{count?.total ?? 0}</span>
              {count && count.ignored > 0 && <span className="ml-1 text-gray-500">({count.ignored} ignored)</span>}
            </button>
          );
        })}
      </div>
      {summary.warnings.length > 0 && <Alert type="warning" message={<ul className="list-disc pl-4">{summary.warnings.map((warning) => <li key={warning}>{warning}</li>)}</ul>} />}
      <Alert type="info" message="Every change is approved unless you ignore it. Debit overrides and multipliers keep applying on top of updated base debits; a release never changes them." />
      <div className="flex flex-wrap items-end justify-between gap-3">
        <SelectInput id="change-kind-filter" label="Show" size="sm" value={kindFilter} onChange={(value) => setKindFilter(value as MugsManualChangeKind | "all")} options={[{ value: "all", label: `All changes (${totalChanges})` }, ...presentKinds.map((kind) => ({ value: kind, label: CHANGE_KIND_LABELS[kind] }))]} containerClassName="w-72" />
        <p className="text-sm text-gray-600">
          <span className="tabular-nums font-medium text-gray-800">{totalChanges}</span> changes, <span className="tabular-nums font-medium text-gray-800">{summary.ignoredCount}</span> ignored
        </p>
      </div>
      <DataGrid caption={`Changes in ${summary.name}`} columns={columns} rows={visible} rowKey={(row) => row.id} isLoading={isLoading} emptyState={<EmptyState message="This release changes nothing in the catalog." />} />
    </div>
  );
}
